import type { ReactNode } from 'react'
import { AlertCircleIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export interface AlertBannerProps {
	title:    ReactNode
	description?: ReactNode
	action?:  ReactNode
	variant?: 'destructive' | 'warning' | 'info'
	icon?:    ReactNode
	className?: string
}

const VARIANT_STYLES: Record<NonNullable<AlertBannerProps['variant']>, { wrap: string; title: string; icon: string }> = {
	destructive: {
		wrap:  'border-(--color-destructive)/30 bg-(--color-destructive)/5',
		title: 'text-(--color-destructive)',
		icon:  'text-(--color-destructive)',
	},
	warning: {
		wrap:  'border-(--color-warning)/30 bg-(--color-warning)/5',
		title: 'text-(--color-warning)',
		icon:  'text-(--color-warning)',
	},
	info: {
		wrap:  'border-(--color-info)/30 bg-(--color-info)/5',
		title: 'text-(--color-info)',
		icon:  'text-(--color-info)',
	},
}

export function AlertBanner({
	title, description, action, variant = 'destructive', icon, className,
}: AlertBannerProps) {
	const styles = VARIANT_STYLES[variant]
	return (
		<div
			className={cn(
				'flex items-start justify-between gap-3 rounded-(--radius-lg) border px-4 py-3',
				styles.wrap,
				className,
			)}
		>
			<div className="flex items-start gap-2">
				<span className={cn('mt-0.5 shrink-0', styles.icon)}>
					{icon ?? <AlertCircleIcon className="size-4" />}
				</span>
				<div className="flex flex-col gap-0.5">
					<span className={cn('text-sm font-[500]', styles.title)}>{title}</span>
					{description && (
						<span className="text-xs text-(--color-muted-foreground)">{description}</span>
					)}
				</div>
			</div>
			{action}
		</div>
	)
}
